import React from "react";
import { WHY_ITEMS, TESTIMONIALS, TECH_STACK } from "../../data";
import { SectionLabel, SectionTitle, SectionSub, StarRating } from "../ui/Typography";
import useScrollFade from "../../hooks/useScrollFade";

/* ── Why Choose Us ───────────────────────────────── */
export const WhyChooseSection = () => {
  const leftRef  = useScrollFade();
  const rightRef = useScrollFade();
  return (
    <section className="py-20 bg-gradient-to-br from-slate-950 via-[#0d2151] to-[#0e2660] relative overflow-hidden">
      <div className="hero-grid-bg absolute inset-0 opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">

          {/* Left */}
          <div ref={leftRef} className="fade-in-section">
            <SectionLabel light>Why Choose Us</SectionLabel>
            <SectionTitle light>
              What Makes Us{" "}
              <span className="text-blue-300">Different</span>
            </SectionTitle>
            <SectionSub light className="mb-8">
              We don't just build software — we build intelligent ecosystems. Every solution is tailored, scalable, and future-proof.
            </SectionSub>
            <div className="space-y-4">
              {WHY_ITEMS.map((item) => (
                <div
                  key={item.title}
                  className="flex gap-4 items-start bg-white/5 border border-white/8 rounded-xl p-4 hover:bg-white/9 hover:translate-x-1 transition-all duration-200"
                >
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center shrink-0 mt-0.5">
                    <svg viewBox="0 0 20 20" fill="white" className="w-3.5 h-3.5">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-head font-semibold text-white text-sm mb-1">{item.title}</div>
                    <div className="text-white/50 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — metrics card */}
          <div ref={rightRef} className="fade-in-section">
            <div className="glass rounded-2xl p-8">
              <div className="font-head font-bold text-white mb-1">Business Impact Dashboard</div>
              <div className="text-white/40 text-sm mb-6">Average client results after AI deployment</div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { val: "38%",  label: "Revenue Increase",       color: "text-orange-400" },
                  { val: "52%",  label: "Operational Efficiency", color: "text-blue-300"  },
                  { val: "99%",  label: "AI Accuracy Rate",       color: "text-emerald-400"},
                  { val: "60d",  label: "Avg Time to Deploy",     color: "text-white"      },
                ].map(m => (
                  <div key={m.label} className="bg-white/6 rounded-xl p-4 text-center border border-white/8">
                    <div className={`font-head font-black text-3xl ${m.color}`}>{m.val}</div>
                    <div className="text-white/40 text-xs mt-1 leading-tight">{m.label}</div>
                  </div>
                ))}
              </div>
              <div className="pt-5 border-t border-white/10">
                <div className="text-white/30 text-xs font-head font-bold uppercase tracking-widest mb-3">Technology Stack</div>
                <div className="flex flex-wrap gap-2">
                  {TECH_STACK.map(t => (
                    <span key={t} className="text-xs font-head font-semibold px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/25 text-blue-300">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Testimonials ────────────────────────────────── */
export const TestimonialsSection = () => {
  const titleRef = useScrollFade();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div ref={titleRef} className="fade-in-section text-center mb-12">
          <SectionLabel>Client Success Stories</SectionLabel>
          <SectionTitle>
            What Our <span className="text-blue-600">Clients Say</span>
          </SectionTitle>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => {
            const ref = useScrollFade();
            return (
              <div
                key={i}
                ref={ref}
                className="fade-in-section card-hover bg-white border border-slate-200 rounded-2xl p-7 relative overflow-hidden"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Quote mark */}
                <span className="absolute top-3 right-5 font-head font-black text-8xl text-blue-100 leading-none pointer-events-none select-none">
                  "
                </span>
                <StarRating count={t.stars} />
                <p className="text-slate-500 text-sm leading-relaxed italic my-4">{t.quote}</p>
                <div className="flex items-center gap-3 mt-auto">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center font-head font-bold text-white shrink-0">
                    {t.initials}
                  </div>
                  <div>
                    <div className="font-head font-semibold text-slate-800 text-sm">{t.name}</div>
                    <div className="text-slate-400 text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
